/**
 * The parts of Billing that are not the seat count — the trial, the move to
 * Enterprise, and monthly or annual. (The invoices are `BillingHistory`, shared with the personal
 * list.)
 *
 * Out of `Organizations.tsx`, which already holds the seat count, the first
 * purchase and the member list, and is past the length anybody reads. Each
 * component here is one card the Billing page places; none of them fetches the
 * organization itself, they are handed it.
 */
import { useState } from 'react';
import type React from 'react';
import { color, font } from '../theme/tokens';
import { daysLeft } from '../lib/billing';
import { Badge, Card, GhostButton, PrimaryButton } from '../components/primitives';
import { api, ApiError, apiPath } from '../lib/api';
import { proseDay } from '../lib/format';
import type { Me, Organization } from '../lib/types';

const DAY = 86_400_000;

/** The plan word a trial carries (`TRIAL_PLAN` in the Worker's `env.ts`). */
export const TRIAL_PLAN = 'trial';

/**
 * Whether this organization may start its trial: never had one, never had a
 * subscription, and no grant holding its seats open. The Worker refuses each of
 * these on its own (`startTrial`); this only decides whether to offer.
 */
export function trialOffered(organization: Organization): boolean {
  const { billing } = organization;
  return (
    billing.trialStartedAt === null &&
    billing.stripeCustomer === null &&
    billing.stripeSubscription === null &&
    (organization.seatAccess === 'none' || organization.seatAccess === 'lapsed')
  );
}

/** The offer: fourteen days, five seats, no card. Only the owner can take it. */
export function TrialOffer({
  me,
  organization,
  onStarted
}: {
  me: Me;
  organization: Organization;
  onStarted: (organization: Organization) => Promise<void>;
}): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { days, seats } = me.config.trial;
  const owner = organization.role === 'owner';

  const start = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const answer = await api.post<{ organization: Organization }>(apiPath`/orgs/${organization.id}/trial`);
      await onStarted(answer.organization);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : 'That did not work.');
      setBusy(false);
    }
  };

  return (
    <Card style={{ maxWidth: 620, marginBottom: 20 }}>
      <div style={{ fontSize: 16, fontWeight: 600 }}>Try it for {days} days</div>
      <p style={{ fontSize: 13.5, color: color.inkSubtle, margin: '6px 0 0', lineHeight: 1.6 }}>
        Up to {seats} seats, with no card and nothing charged. You get a seat straight away; give the others on
        Members. When it ends, buying seats keeps everybody on the same key.
      </p>
      {owner ? (
        <div style={{ marginTop: 18 }}>
          <PrimaryButton onClick={() => void start()} disabled={busy}>
            {busy ? 'Starting' : `Start the ${days}-day trial`}
          </PrimaryButton>
        </div>
      ) : (
        <p style={{ fontSize: 13, color: color.inkFaint, margin: '14px 0 0' }}>Only the owner can start it.</p>
      )}
      {error && <p style={{ fontSize: 13, color: color.badText, margin: '12px 0 0' }}>{error}</p>}
    </Card>
  );
}

/** A running (or finished) trial: days left, seats given, and that buying is below. */
/**
 * A pill outside a table (redesign §8): 22 high, the tone's wash, a 6 dot — or,
 * for the plan, a ring with no fill, since a plan is a name and not a state.
 */
function StatusPill({
  tone,
  ring = false,
  children
}: {
  tone: 'ok' | 'warn' | 'off';
  ring?: boolean;
  children: React.ReactNode;
}): React.JSX.Element {
  const [ink, wash, dot] = {
    ok: [color.okText, color.okBg, color.accent],
    warn: [color.warnText, color.warnBg, '#C9A03A'],
    off: [color.inkMuted, color.offBg, color.lineInput]
  }[tone];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        height: 22,
        padding: '0 8px',
        borderRadius: 999,
        background: ring ? 'transparent' : wash,
        boxShadow: ring ? `inset 0 0 0 1px ${color.line}` : 'none',
        fontSize: 12.5,
        fontWeight: 500,
        color: ring ? color.inkMuted : ink,
        whiteSpace: 'nowrap'
      }}
    >
      {!ring && <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', background: dot }} />}
      {children}
    </span>
  );
}

/**
 * The subscription's state under the page title (redesign §43.3): the plan in a
 * ring, the state in a washed pill (Active, Trial, Trial ended, Seats granted),
 * then a 14.5 lede saying the date and what comes next. It replaces the trial's
 * card and the grant's teal callout, and the trial banner of the other pages is
 * not repeated here.
 */
export function BillingStatus({
  me,
  organization,
  paying,
  annual
}: {
  me: Me;
  organization: Organization;
  paying: boolean;
  annual: boolean;
}): React.JSX.Element | null {
  const { billing } = organization;
  const onTrial = !paying && billing.plan === TRIAL_PLAN;
  const granted = !paying && !onTrial && organization.seatAccess !== 'none';
  if (!paying && !onTrial && !granted) return null;
  const left = billing.paidThrough !== null && billing.paidThrough > Date.now() ? Math.max(1, daysLeft(billing.paidThrough)) : 0;
  const day = billing.trialStartedAt === null ? 1 : Math.max(1, Math.ceil((Date.now() - Date.parse(billing.trialStartedAt)) / DAY));

  const pills = paying ? (
    <>
      <StatusPill tone="off" ring>
        {billing.tier === 'enterprise' ? 'Enterprise' : 'Team'}
      </StatusPill>
      <StatusPill tone={organization.seatAccess === 'active' ? 'ok' : 'warn'}>
        {organization.seatAccess === 'active' ? 'Active' : organization.seatAccess === 'grace' ? 'Grace' : organization.seatAccess}
      </StatusPill>
      {billing.cancelAtPeriodEnd && <StatusPill tone="warn">ends at period end</StatusPill>}
    </>
  ) : onTrial ? (
    <StatusPill tone={left > 0 ? 'ok' : 'warn'}>
      {left > 0 ? `Trial · ${left} day${left === 1 ? '' : 's'} left` : 'Trial ended'}
    </StatusPill>
  ) : (
    <StatusPill tone="off">Seats granted</StatusPill>
  );

  const lede = paying ? (
    annual && billing.paidThrough !== null ? (
      `The next yearly invoice is on ${proseDay(billing.paidThrough)}. Adding a seat is prorated to that day; giving one back is credited.`
    ) : billing.paidThrough !== null ? (
      `This period is paid through ${proseDay(billing.paidThrough)}.`
    ) : null
  ) : onTrial ? (
    left > 0 ? (
      <>
        <span style={{ fontFamily: font.mono, fontSize: 13, color: color.inkFaint }}>
          day {Math.min(day, me.config.trial.days)} of {me.config.trial.days} · runs to {proseDay(billing.paidThrough)}
        </span>
        <br />
        {organization.seats} of {me.config.trial.seats} seats are given out. Buy seats below whenever you are ready — nobody’s
        key changes.
      </>
    ) : (
      `It ended on ${proseDay(billing.paidThrough)}. Seats keep working until ${proseDay(billing.expiresAt)}; buy below to keep them.`
    )
  ) : (
    `Airlock granted ${organization.name} its access rather than selling it${
      billing.paidThrough ? `, until ${proseDay(billing.paidThrough)}` : ''
    }. Nothing is charged for it; buy seats below to keep going after that.`
  );

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>{pills}</div>
      {lede && <p style={{ fontSize: 14.5, lineHeight: 1.55, color: color.inkMuted, margin: '10px 0 0', maxWidth: 620 }}>{lede}</p>}
    </div>
  );
}

/** A figure as the band says it: « $4,200 ». */
function usd(value: number): string {
  return `$${value.toLocaleString('en-US')}`;
}

/**
 * The confirmation band of the redesign (§22, §43.2): what changes, before →
 * after with the price, then the refusal and the verb — the gerund on the verb
 * while it goes. The control above it applies nothing by itself.
 */
function ConfirmBand({
  label,
  text,
  verb,
  going,
  busy,
  onCancel,
  onConfirm
}: {
  label: string;
  text: string;
  verb: string;
  going: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}): React.JSX.Element {
  return (
    <div
      role="group"
      aria-label={label}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
        marginTop: 14,
        padding: '12px 14px',
        border: `1px solid ${color.line}`,
        borderRadius: 12
      }}
    >
      <span style={{ flex: 1, minWidth: 220, fontSize: 13, color: color.inkMuted, fontVariantNumeric: 'tabular-nums' }}>
        {text}
      </span>
      <GhostButton size="sm" onClick={onCancel} disabled={busy}>
        Not now
      </GhostButton>
      <PrimaryButton size="sm" onClick={onConfirm} disabled={busy}>
        {busy ? going : verb}
      </PrimaryButton>
    </div>
  );
}

/**
 * Team to Enterprise, for an organization already paying for Team seats.
 *
 * The owner's button; an administrator reads the offer and is told who can
 * take it. Stripe prorates the difference and bills it at once, and the
 * subscription moves only once that invoice is paid (`upgradeToEnterprise`) —
 * so the answer may be "waiting on payment", which is said rather than hidden.
 * The button only unfolds the confirmation band (§43.2): the seats and the
 * price before → after, Not now, then the move itself.
 */
export function EnterpriseUpgrade({
  me,
  organization,
  onMoved
}: {
  me: Me;
  organization: Organization;
  onMoved: (organization: Organization) => Promise<void>;
}): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const plan = me.config.enterprisePlans.find((candidate) => candidate.id === organization.billing.plan);
  const team = me.config.seatPlans.find((candidate) => candidate.id === organization.billing.plan);
  const owner = organization.role === 'owner';
  const yearly = organization.billing.plan === 'annual';
  const seats = Math.max(1, organization.billing.seatsPaid);

  const move = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const answer = await api.post<{ tier: string; pending: boolean; organization: Organization }>(
        apiPath`/orgs/${organization.id}/billing/enterprise`
      );
      setNote(
        answer.tier === 'enterprise'
          ? 'Enterprise from today. Single sign-on, SCIM and enforced two-factor are open on Security.'
          : 'The move is waiting on the payment for it. It completes as soon as the invoice is paid.'
      );
      setConfirming(false);
      await onMoved(answer.organization);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card style={{ maxWidth: 620, marginTop: 20 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <span style={{ fontSize: 16, fontWeight: 600 }}>Move to Enterprise</span>
        {plan && (
          <span style={{ fontFamily: font.mono, fontSize: 12, color: color.inkMuted }}>
            ${plan.priceUsd} {plan.period}
          </span>
        )}
      </div>
      <p style={{ fontSize: 13.5, color: color.inkSubtle, margin: '8px 0 0', lineHeight: 1.6 }}>
        Everything in Team, plus single sign-on (SAML and OIDC), SCIM provisioning, enforced two-factor, verified
        domains, audit streaming to your SIEM, and paying by invoice.{' '}
        {`Moving now is prorated for the rest of this ${yearly ? 'year' : 'month'}; nobody’s key changes and every seat carries over.`}
      </p>
      {!plan?.onSale ? (
        <p style={{ fontSize: 13, color: color.warnText, margin: '12px 0 0' }}>
          Not on sale on this deployment yet — no Stripe price is configured for Enterprise.
        </p>
      ) : !owner ? (
        <p style={{ fontSize: 13, color: color.inkFaint, margin: '14px 0 0' }}>Only the owner can move it.</p>
      ) : confirming ? (
        <ConfirmBand
          label="Confirm the move to Enterprise"
          text={`${seats} seat${seats === 1 ? '' : 's'}: ${team ? `${usd(team.priceUsd * seats)} → ` : ''}${usd(plan.priceUsd * seats)} a ${yearly ? 'year' : 'month'}, excl. VAT`}
          verb="Move to Enterprise"
          going="Moving"
          busy={busy}
          onCancel={() => setConfirming(false)}
          onConfirm={() => void move()}
        />
      ) : (
        <div style={{ marginTop: 18 }}>
          <PrimaryButton
            onClick={() => {
              setNote(null);
              setConfirming(true);
            }}
          >
            Move to Enterprise
          </PrimaryButton>
        </div>
      )}
      {note && <p style={{ fontSize: 13, color: color.okText, margin: '12px 0 0', lineHeight: 1.6 }}>{note}</p>}
      {error && <p style={{ fontSize: 13, color: color.badText, margin: '12px 0 0' }}>{error}</p>}
    </Card>
  );
}

/**
 * Monthly or annual, for an organization already paying (`billing-period.ts`
 * in the Worker). The tier stays what it is; only the period moves.
 *
 * The period is a segmented Monthly / Yearly (§43.2): choosing the other one
 * applies nothing, it unfolds the confirmation band with the figure, Not now
 * and the verb. The two directions say different things because they cost
 * different things: yearly is bought now — the rest of the month credited, the
 * year invoiced; monthly waits for the paid year to end, so the card says the
 * date it will happen on and offers to call it off until then. Only the owner
 * is offered either; an administrator reads what is set and who can change it.
 */
export function PeriodSwitch({
  me,
  organization,
  onMoved
}: {
  me: Me;
  organization: Organization;
  onMoved: (organization: Organization) => Promise<void>;
}): React.JSX.Element | null {
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { billing } = organization;
  const plans = billing.tier === 'enterprise' ? me.config.enterprisePlans : me.config.seatPlans;
  const current = plans.find((candidate) => candidate.id === billing.plan);
  if (!current) return null;
  const other = plans.find((candidate) => candidate.id !== current.id);
  const owner = organization.role === 'owner';
  const pending = billing.pendingPlan !== null && billing.pendingPlanAt !== null;
  const yearly = current.id === 'annual';

  const act = async (method: 'post' | 'del', plan?: string): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const path = apiPath`/orgs/${organization.id}/billing/period`;
      const answer =
        method === 'post'
          ? await api.post<{ waiting: boolean; pendingPlanAt: number | null; organization: Organization }>(path, { plan })
          : await api.del<{ waiting: boolean; pendingPlanAt: number | null; organization: Organization }>(path);
      setNote(
        method === 'del'
          ? 'Called off — the year renews as a year.'
          : answer.waiting
            ? 'The move to yearly is waiting on the payment for it. It completes as soon as the invoice is paid.'
            : plan === 'annual'
              ? `Yearly from ${proseDay(Date.now())}. The first yearly invoice is on its way.`
              : `Monthly billing starts on ${proseDay(answer.pendingPlanAt)}, when the paid year ends. Nothing is refunded.`
      );
      setConfirming(false);
      await onMoved(answer.organization);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  const seats = Math.max(1, billing.seatsPaid);
  const open = owner && !pending && !billing.cancelAtPeriodEnd && other?.onSale === true;
  const shown = confirming ? !yearly : yearly;
  const band = !other
    ? ''
    : yearly
      ? `${usd(current.priceUsd * seats)} / year → ${usd(other.priceUsd * seats)} / month for ${seats} seat${seats === 1 ? '' : 's'}, from ${proseDay(billing.paidThrough)} when the paid year ends`
      : `${usd(current.priceUsd * seats)} / month → ${usd(other.priceUsd * seats)} / year for ${seats} seat${seats === 1 ? '' : 's'}, less what is left of this month`;
  const segment = (value: boolean, word: string): React.JSX.Element => {
    const on = shown === value;
    return (
      <button
        type="button"
        role="radio"
        aria-checked={on}
        disabled={!open}
        onClick={() => {
          setNote(null);
          setConfirming(value !== yearly);
        }}
        style={{
          height: 26,
          padding: '0 14px',
          border: 0,
          borderRadius: 6,
          background: on ? color.surface : 'transparent',
          boxShadow: on ? '0 1px 2px rgba(22,25,31,.06)' : 'none',
          fontFamily: 'inherit',
          fontSize: 12.5,
          fontWeight: on ? 600 : 400,
          color: on ? color.ink : color.inkSubtle,
          cursor: open ? 'pointer' : 'default',
          transition: 'background .12s, color .12s'
        }}
      >
        {word}
      </button>
    );
  };

  return (
    <Card style={{ maxWidth: 620, marginTop: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 16, fontWeight: 600 }}>Billed {yearly ? 'yearly' : 'monthly'}</span>
        <span style={{ fontFamily: font.mono, fontSize: 12, color: color.inkMuted }}>
          ${current.priceUsd} {current.period}
        </span>
        {pending && <Badge tone="warn">monthly from {proseDay(billing.pendingPlanAt)}</Badge>}
        <span style={{ flex: 1 }} />
        <div
          role="radiogroup"
          aria-label="Billing period"
          style={{ display: 'flex', alignItems: 'center', gap: 2, padding: 2, border: `1px solid ${color.line}`, borderRadius: 8, background: color.surfaceAlt }}
        >
          {segment(false, 'Monthly')}
          {segment(true, 'Yearly')}
        </div>
      </div>
      <p style={{ fontSize: 13.5, color: color.inkSubtle, margin: '8px 0 0', lineHeight: 1.6 }}>
        {pending
          ? `The year paid for runs to ${proseDay(billing.pendingPlanAt)}, and monthly billing starts that day — nothing is refunded. Same seats, same keys.`
          : yearly
            ? `Moving to monthly waits for the paid year to end${billing.paidThrough ? ` on ${proseDay(billing.paidThrough)}` : ''}: nothing is refunded, and the first monthly invoice comes that day.`
            : other
              ? `Yearly is $${other.priceUsd} ${other.period}. Moving now credits what is left of this month and invoices the year at once.`
              : ''}
      </p>
      {!owner ? (
        <p style={{ fontSize: 13, color: color.inkFaint, margin: '14px 0 0' }}>Only the owner can change how it is billed.</p>
      ) : pending ? (
        <div style={{ marginTop: 18 }}>
          <GhostButton onClick={() => void act('del')} disabled={busy}>
            {busy ? 'Keeping' : 'Keep yearly billing'}
          </GhostButton>
        </div>
      ) : billing.cancelAtPeriodEnd ? (
        <p style={{ fontSize: 13, color: color.inkFaint, margin: '14px 0 0' }}>
          The subscription ends with this period, so the billing period cannot change. Resume it in the billing portal first.
        </p>
      ) : !other?.onSale ? (
        <p style={{ fontSize: 13, color: color.warnText, margin: '12px 0 0' }}>
          {yearly ? 'Monthly' : 'Yearly'} billing is not on sale on this deployment yet — no Stripe price is configured for it.
        </p>
      ) : confirming ? (
        <ConfirmBand
          label="Confirm the billing period"
          text={band}
          verb={yearly ? 'Switch to monthly at renewal' : 'Switch to yearly'}
          going="Switching"
          busy={busy}
          onCancel={() => setConfirming(false)}
          onConfirm={() => void act('post', yearly ? 'monthly' : 'annual')}
        />
      ) : null}
      {note && <p style={{ fontSize: 13, color: color.okText, margin: '12px 0 0', lineHeight: 1.6 }}>{note}</p>}
      {error && <p style={{ fontSize: 13, color: color.badText, margin: '12px 0 0' }}>{error}</p>}
    </Card>
  );
}
